import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { DIMENSOES, FORMATIVA_VALORES, fmtNota, formativaBtn, formativaBadge } from "@/lib/constants";
import { Save, CheckCircle2, Loader2, ArrowLeft, Layers, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function FormativaPad() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [av, setAv] = useState(null);
  const [notas, setNotas] = useState({}); // criterio_id -> number
  const [obs, setObs] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/formativa/avaliacoes/${id}`).then((r) => {
      const a = r.data;
      setAv(a);
      const map = {};
      (a.notas || []).forEach((x) => { if (x.nota !== null && x.nota !== undefined) map[x.criterio_id] = x.nota; });
      setNotas(map);
      setObs(a.observacoes || "");
    });
  }, [id]);

  const setNota = (cid, v) =>
    setNotas((r) => ({ ...r, [cid]: r[cid] === v ? undefined : v }));

  const buildPayload = useCallback(() => ({
    notas: (av?.criterios || [])
      .map((c) => ({ criterio_id: c.id, nota: notas[c.id] ?? null }))
      .filter((n) => n.nota !== null),
    observacoes: obs,
  }), [av, notas, obs]);

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/formativa/avaliacoes/${id}`, buildPayload());
      setAv(data);
      if (!silent) toast.success("Progresso salvo");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const finalizar = async () => {
    setSaving(true);
    try {
      await api.put(`/formativa/avaliacoes/${id}`, buildPayload());
      const { data } = await api.post(`/formativa/avaliacoes/${id}/finalizar`);
      setAv(data);
      toast.success("Avaliação formativa finalizada");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!av) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const voltarAlunos = () => navigate("/formativa", { state: { cursoId: av.curso_id, turmaId: av.turma_id, cursoNome: av.curso_nome, turmaNome: av.turma_nome } });

  const criterios = av.criterios || [];
  const answered = criterios.filter((c) => notas[c.id] !== undefined && notas[c.id] !== null).length;
  const soma = criterios.reduce((acc, c) => acc + (notas[c.id] ?? 0), 0);
  const media = answered ? soma / answered : null;

  // agrupa por dimensão
  const grupos = [];
  const gmap = {};
  criterios.forEach((c) => {
    const g = c.dimensao;
    if (!gmap[g]) { gmap[g] = []; grupos.push(g); }
    gmap[g].push(c);
  });

  const exportar = (fmt) => {
    const base = (av.aluno_nome || "aluno").replace(/\s+/g, "_");
    downloadFile(`/formativa/avaliacoes/${id}/export/${fmt}`, `MÉTRON_Formativa_${base}.${fmt}`)
      .catch(() => toast.error("Falha ao exportar"));
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6 items-start">
      {/* Sidebar contexto */}
      <div className="lg:col-span-4 lg:sticky lg:top-6 space-y-4">
        <button data-testid="fvoltar-btn" onClick={voltarAlunos} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Voltar para alunos
        </button>
        <Card className="p-5 bg-card border-border">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">Aluno</div>
          <div className="font-heading text-2xl font-bold">{av.aluno_nome}</div>
          {av.aluno_nome_guerra && <div className="text-sm text-primary font-mono">"{av.aluno_nome_guerra}"</div>}
          <div className="mt-4 space-y-1.5 text-sm">
            <Row label="Curso" value={av.curso_nome} />
            <Row label="Turma" value={av.turma_nome} />
            <Row label="Instrutor" value={av.avaliador_nome} />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-secondary/60 p-3 text-center">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Soma</div>
              <div className="font-heading text-2xl font-bold" data-testid="fsoma-valor">{fmtNota(soma)}</div>
            </div>
            <div className="rounded-lg bg-secondary/60 p-3 text-center">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Média</div>
              <div className="font-heading text-2xl font-bold" data-testid="fmedia-valor">
                <Badge className={`text-lg px-2 ${formativaBadge(media)}`}>{fmtNota(media)}</Badge>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex justify-between text-xs mb-1.5">
              <span className="text-muted-foreground">Progresso</span>
              <span className="font-mono">{answered}/{criterios.length}</span>
            </div>
            <div className="h-2 rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${criterios.length ? (answered / criterios.length) * 100 : 0}%` }} />
            </div>
          </div>

          {av.status === "concluida" && (
            <Badge className="mt-4 bg-teal-500/20 text-teal-300 border-teal-500/40">Concluída</Badge>
          )}
        </Card>

        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" data-testid="fsalvar-btn" onClick={() => save(false)} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Salvar
          </Button>
          <Button className="flex-1" data-testid="ffinalizar-btn" onClick={finalizar} disabled={saving || answered === 0}>
            <CheckCircle2 className="h-4 w-4 mr-2" />Finalizar
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1" data-testid="fexport-docx" onClick={() => exportar("docx")}>
            <FileText className="h-4 w-4 mr-2" /> Word
          </Button>
          <Button variant="outline" size="sm" className="flex-1" data-testid="fexport-xlsx" onClick={() => exportar("xlsx")}>
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
          </Button>
        </div>
      </div>

      {/* Critérios agrupados por dimensão */}
      <div className="lg:col-span-8 space-y-6">
        {grupos.map((g) => {
          const D = DIMENSOES[g];
          const crits = gmap[g];
          return (
            <div key={g}>
              <div className="flex items-center gap-2 mb-3">
                <Layers className="h-5 w-5 text-primary" />
                <h2 className="font-heading text-xl font-bold uppercase tracking-tight">{D?.label || g}</h2>
                <span className="text-xs text-muted-foreground font-mono">{crits.length}</span>
              </div>
              <div className="space-y-3">
                {crits.map((c) => {
                  const val = notas[c.id];
                  return (
                    <Card key={c.id} data-testid={`fcriterio-card-${c.id}`} className="p-4 bg-card border-border">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="min-w-0">
                          <div className="font-medium">{c.nome}</div>
                          {D && <Badge className={`${D.badge} text-[10px] mt-1`}>{D.label}</Badge>}
                        </div>
                        {val !== undefined && (
                          <Badge className={`${formativaBadge(val)} shrink-0`} data-testid={`fnota-atual-${c.id}`}>{fmtNota(val)}</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {FORMATIVA_VALORES.map((v) => {
                          const active = val === v;
                          return (
                            <button key={v} data-testid={`fnota-${c.id}-${v}`} onClick={() => setNota(c.id, v)}
                              className={`h-9 min-w-[38px] px-1 rounded-md text-white text-xs font-semibold flex items-center justify-center transition-all active:scale-95 ${formativaBtn(v)} ${active ? "ring-2 ring-offset-2 ring-offset-card scale-105" : "opacity-70 hover:opacity-100"}`}>
                              {fmtNota(v)}
                            </button>
                          );
                        })}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}

        <Card className="p-4 bg-card border-border space-y-3">
          <h3 className="font-heading text-lg font-bold uppercase tracking-tight">Observações</h3>
          <Textarea data-testid="fobs-geral" value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Observações do instrutor sobre o desempenho do aluno..." />
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right truncate">{value}</span>
    </div>
  );
}
